import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal, ProfileDetails } from "../v3";
import { AccountService, HumanResourceService } from "../../services";
import { EDIT_EMPLOYEE, DELETE_EMPLOYEE } from "../../constants";
import { AddEditEmployee } from "./AddEditEmployee.component";
import { formatBalance, getBalanceColor, useHasPermission } from "../../utils";
import { EMPLOYEE_AVATAR } from "../../utils/image.constants";

export const EmployeeDetails = ({ reloadData = 0 }) => {
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    employeeId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();

  const hadEmployeeEdit = useHasPermission(EDIT_EMPLOYEE);
  const hasEmployeeDelete = useHasPermission(DELETE_EMPLOYEE);

  useEffect(() => {
    fetchData(id);
  }, [id, reloadData]);

  const fetchData = async (employeeId) => {
    try {
      const response = await HumanResourceService.getEmployeeById(employeeId);
      setEmployeeDetails(response.data);
      setIsActive(response?.data?.active);

      const accountId = response?.data?.accountId;
      if (accountId) {
        const accountResponse = await AccountService.getAccountById(accountId);
        setEmployeeDetails((prev) => ({
          ...prev,
          balance: accountResponse?.data?.balance,
        }));
      }
    } catch (error) {
      toast("Failed to fetch employee details", { type: "error" });
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      employeeId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      employeeId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await HumanResourceService.deleteEmployee(deleteModal.employeeId);
      toast.success("Employee deleted successfully");
      closeDeleteModal();
      navigate(-1);
    } catch (error) {
      toast.error("Failed to delete employee");
      console.error("🚀 ~ deleteStatement ~ error:", error);
    }
  };

  const handleStatusChange = async (e) => {
    const updatedStatus = e.target.checked;
    try {
      await HumanResourceService.updateEmployee(id, { active: updatedStatus });
      setIsActive(updatedStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("🚀 ~ handleStatusChange ~ error:", error);
    }
  };

  return (
    <>
      <ProfileDetails
        profileImage={EMPLOYEE_AVATAR}
        name={employeeDetails?.name}
        subtitle={employeeDetails?.designation}
        phone={employeeDetails?.primaryPhone ? employeeDetails.primaryPhone : "not available"}
        isActive={isActive}
        canToggle={hadEmployeeEdit}
        canEdit={hadEmployeeEdit}
        canDelete={hasEmployeeDelete}
        onEdit={handleOpenModal}
        onDelete={openDeleteModal}
        onToggle={(e) => handleStatusChange(e)}
        infoFields={[
          {
            label: "Balance",
            value: formatBalance(employeeDetails?.balance),
            color: getBalanceColor(employeeDetails?.balance),
          },
          { label: "Salary", value: employeeDetails?.salary ? `₹ ${employeeDetails.salary}` : "-" },
        ]}
      />

      {/* edit employee modal */}
      <AddEditEmployee employeeId={id} open={openModal} onClose={handleCloseModal} onSuccess={() => fetchData(id)} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this employee?"
      />
    </>
  );
};
